import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Users, Check, X as XIcon, Headphones, ShoppingCart, EyeOff, Ban } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { Avatar } from '../../../components/Avatar';
import { useToast } from '../../../components/Toast';
import { useDialog } from '../../../components/DialogProvider';
import { atendimentoApi } from '../lib/api';
import { useEtiquetas } from '../lib/etiquetas-context';
import { BadgeEtiqueta } from '../componentes/BadgeEtiqueta';
import type { Contato, Inbox } from '../lib/tipos';

interface Props {
  aberto: boolean;
  onFechar: () => void;
  // embutido: renderiza so o conteudo (sem o wrapper Modal), pra encaixar numa aba.
  embutido?: boolean;
}

interface DraftContato {
  id: number;
  nome: string;
  sobrenome: string;
  telefone: string;
  email: string;
  etiquetaIds: number[];
  bloqueado: boolean;
  oculto: boolean;
  cliente_id: number | null;
}

export function ModalContatos({ aberto, onFechar, embutido = false }: Props) {
  const toast = useToast();
  const dialog = useDialog();
  // Idem PainelContato: `visiveis` governa a exibicao, e `draft.etiquetaIds` (montado do
  // proprio contato) carrega as ocultas, entao salvar em lote nao as apaga.
  const { visiveis: todasEtiquetas } = useEtiquetas();

  const [lista, setLista] = useState<Contato[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [inboxFiltro, setInboxFiltro] = useState<Inbox | ''>('');
  const [mostrarOcultos, setMostrarOcultos] = useState(false);
  const [semGrupo, setSemGrupo] = useState(true);
  const [etiquetaFiltro, setEtiquetaFiltro] = useState<number | null>(null);

  const [expandidoId, setExpandidoId] = useState<number | null>(null);
  const [draft, setDraft] = useState<DraftContato | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function recarregar() {
    setCarregando(true);
    try {
      const r = await atendimentoApi.listarContatos({
        busca: busca.trim() || undefined,
        inbox: inboxFiltro || undefined,
        mostrar_ocultos: mostrarOcultos,
        sem_grupo: semGrupo,
        etiqueta_id: etiquetaFiltro ?? undefined,
        limit: 500,
      });
      setLista(r);
    } catch {
      toast.erro('Falha ao carregar contatos');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    if (!aberto) return;
    setExpandidoId(null);
    setDraft(null);
    recarregar();
  }, [aberto]);

  // Recarrega com debounce ao mudar filtros
  useEffect(() => {
    if (!aberto) return;
    const t = setTimeout(() => recarregar(), 300);
    return () => clearTimeout(t);
  }, [busca, inboxFiltro, mostrarOcultos, semGrupo, etiquetaFiltro, aberto]);

  function expandir(c: Contato) {
    if (expandidoId === c.id) {
      setExpandidoId(null);
      setDraft(null);
      return;
    }
    setExpandidoId(c.id);
    setDraft({
      id: c.id,
      nome: c.nome ?? '',
      sobrenome: c.sobrenome ?? '',
      telefone: c.telefone ?? '',
      email: c.email ?? '',
      etiquetaIds: (c.etiquetas ?? []).map((e) => e.id),
      bloqueado: c.bloqueado,
      oculto: c.oculto,
      cliente_id: c.cliente_id,
    });
  }

  async function salvar(c: Contato) {
    if (!draft) return;
    setSalvando(true);
    try {
      const original = c;
      const patches: Promise<unknown>[] = [];

      // A troca de numero e' conferida com o WhatsApp NO SERVIDOR (o PUT grava telefone + endereco
      // de envio, 24/09/2026); a recusa volta como erro e aparece no alerta do `catch` abaixo.
      const camposCambiaram =
        draft.nome.trim() !== (original.nome ?? '') ||
        (draft.sobrenome.trim() || null) !== (original.sobrenome ?? null) ||
        (draft.telefone.trim() || null) !== (original.telefone ?? null) ||
        (draft.email.trim() || null) !== (original.email ?? null) ||
        draft.bloqueado !== original.bloqueado ||
        draft.oculto !== original.oculto;

      if (camposCambiaram) {
        patches.push(atendimentoApi.atualizarContato(c.id, {
          nome: draft.nome.trim(),
          sobrenome: draft.sobrenome.trim() || null,
          telefone: draft.telefone.trim() || null,
          email: draft.email.trim() || null,
          bloqueado: draft.bloqueado,
          oculto: draft.oculto,
        }));
      }

      const etqAntigos = (original.etiquetas ?? []).map((e) => e.id).sort().join(',');
      const etqNovos = [...draft.etiquetaIds].sort().join(',');
      if (etqAntigos !== etqNovos) {
        patches.push(atendimentoApi.atualizarEtiquetasContato(c.id, draft.etiquetaIds));
      }

      if (patches.length === 0) {
        setExpandidoId(null);
        setDraft(null);
        return;
      }
      await Promise.all(patches);
      toast.sucesso('Contato atualizado');
      setExpandidoId(null);
      setDraft(null);
      await recarregar();
    } catch (e: any) {
      // Alerta, nao toast: a recusa da troca de numero (sem WhatsApp, ja e' de outro contato) diz
      // o que fazer, e toast de 4s nao se rele.
      await dialog.alerta(e?.message ?? 'Falha ao salvar', { titulo: 'Não salvou' });
    } finally {
      setSalvando(false);
    }
  }

  async function desvincularCliente(c: Contato) {
    const ok = await dialog.confirmar('Desvincular este contato do cliente?', {
      titulo: 'Desvincular cliente?',
      textoConfirmar: 'Desvincular',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await atendimentoApi.atualizarContato(c.id, { cliente_id: null });
      toast.sucesso('Desvinculado');
      await recarregar();
      if (draft && draft.id === c.id) setDraft({ ...draft, cliente_id: null });
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao desvincular');
    }
  }

  const total = useMemo(() => lista.length, [lista]);

  const corpo = (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nome, telefone, JID..."
            className="w-full pl-8 pr-3 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
            style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
          />
        </div>
        <select
          value={inboxFiltro}
          onChange={(e) => setInboxFiltro((e.target.value || '') as Inbox | '')}
          className="px-2 py-1.5 rounded border text-sm"
          style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '14px' }}
        >
          <option value="">Todos os inboxes</option>
          <option value="atendimento">Atendimento</option>
          <option value="compras">Compras</option>
        </select>
        <select
          value={etiquetaFiltro ?? ''}
          onChange={(e) => setEtiquetaFiltro(e.target.value ? Number(e.target.value) : null)}
          className="px-2 py-1.5 rounded border text-sm"
          style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '14px' }}
        >
          <option value="">Todas etiquetas</option>
          {todasEtiquetas.map((et) => <option key={et.id} value={et.id}>{et.apelido ?? et.nome}</option>)}
        </select>
        <label className="flex items-center gap-1 text-xs cursor-pointer">
          <input type="checkbox" checked={semGrupo} onChange={(e) => setSemGrupo(e.target.checked)} className="w-3.5 h-3.5" />
          Sem grupos
        </label>
        <label className="flex items-center gap-1 text-xs cursor-pointer">
          <input type="checkbox" checked={mostrarOcultos} onChange={(e) => setMostrarOcultos(e.target.checked)} className="w-3.5 h-3.5" />
          Mostrar ocultos
        </label>
      </div>

      <div className="text-[11px] opacity-60 mb-2">{total} contato{total === 1 ? '' : 's'}</div>

      {carregando && (
        <div className="flex items-center justify-center py-8 opacity-70">
          <Loader2 size={18} className="animate-spin" />
        </div>
      )}

      {!carregando && lista.length === 0 && (
        <div className="text-center py-8 opacity-60 text-sm">
          <Users size={28} className="mx-auto mb-2 opacity-50" />
          Nenhum contato encontrado.
        </div>
      )}

      {!carregando && lista.length > 0 && (
        <div className="space-y-1 max-h-[55vh] overflow-y-auto pr-1">
          {lista.map((c) => {
            const expandido = expandidoId === c.id;
            const titulo = [c.nome, c.sobrenome].filter(Boolean).join(' ') || c.push_name || c.telefone || c.jid;
            const InboxIco = c.inbox === 'compras' ? ShoppingCart : Headphones;
            return (
              <div key={c.id} className="rounded-lg" style={{ backgroundColor: expandido ? 'rgba(0,0,0,0.04)' : 'transparent', border: '1px solid var(--border, rgba(0,0,0,0.08))' }}>
                <button
                  type="button"
                  onClick={() => expandir(c)}
                  className="w-full flex items-center gap-2.5 p-2 text-left hover:bg-black/5 rounded-lg transition"
                >
                  <Avatar nome={titulo} fotoUrl={c.foto_url} tamanho={32} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium truncate">{titulo}</span>
                      <InboxIco size={11} className="opacity-50 flex-shrink-0" />
                      {c.bloqueado && <Ban size={11} className="text-red-500 flex-shrink-0" />}
                      {c.oculto && <EyeOff size={11} className="opacity-40 flex-shrink-0" />}
                      {c.cliente_id && <span className="text-[10px] px-1 rounded" style={{ backgroundColor: '#fde68a', color: '#000' }}>Cliente</span>}
                    </div>
                    <div className="text-[11px] opacity-70 truncate flex items-center gap-1.5">
                      <span>{c.telefone || c.jid}</span>
                      {(c.etiquetas ?? []).filter((et) => !et.ocultar).map((et) => <BadgeEtiqueta key={et.id} nome={et.nome} cor={et.cor} apelido={et.apelido} />)}
                    </div>
                  </div>
                </button>

                {expandido && draft && (
                  <div className="p-3 border-t" style={{ borderColor: 'var(--border, rgba(0,0,0,0.08))' }}>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-[11px] opacity-70 block mb-0.5">Nome</label>
                        <input
                          type="text"
                          value={draft.nome}
                          onChange={(e) => setDraft({ ...draft, nome: e.target.value })}
                          className="w-full px-2 py-1 rounded border text-sm"
                          style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
                        />
                      </div>
                      <div>
                        <label className="text-[11px] opacity-70 block mb-0.5">Sobrenome</label>
                        <input
                          type="text"
                          value={draft.sobrenome}
                          onChange={(e) => setDraft({ ...draft, sobrenome: e.target.value })}
                          className="w-full px-2 py-1 rounded border text-sm"
                          style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
                        />
                      </div>
                      <div>
                        <label className="text-[11px] opacity-70 block mb-0.5">Telefone</label>
                        <input
                          type="tel"
                          value={draft.telefone}
                          onChange={(e) => setDraft({ ...draft, telefone: e.target.value })}
                          className="w-full px-2 py-1 rounded border text-sm"
                          style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
                        />
                      </div>
                      <div>
                        <label className="text-[11px] opacity-70 block mb-0.5">Email</label>
                        <input
                          type="email"
                          value={draft.email}
                          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                          className="w-full px-2 py-1 rounded border text-sm"
                          style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
                        />
                      </div>
                    </div>

                    <div className="mb-2">
                      <div className="text-[11px] opacity-70 mb-0.5">Etiquetas</div>
                      <div className="flex flex-wrap gap-1">
                        {todasEtiquetas.map((et) => {
                          const ativa = draft.etiquetaIds.includes(et.id);
                          return (
                            <button
                              key={et.id}
                              type="button"
                              onClick={() => setDraft({
                                ...draft,
                                etiquetaIds: ativa ? draft.etiquetaIds.filter((id) => id !== et.id) : [...draft.etiquetaIds, et.id],
                              })}
                              className="px-2 py-1 rounded border text-xs flex items-center gap-1.5 transition"
                              style={{
                                backgroundColor: ativa ? '#fde68a' : 'transparent',
                                borderColor: ativa ? '#fbbf24' : 'var(--border, rgba(0,0,0,0.15))',
                                color: ativa ? '#000' : undefined,
                              }}
                            >
                              <BadgeEtiqueta nome={et.nome} cor={et.cor} apelido={et.apelido} />
                              {et.nome}
                            </button>
                          );
                        })}
                        {todasEtiquetas.length === 0 && <span className="text-[11px] opacity-50">Nenhuma etiqueta cadastrada</span>}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mb-2 items-center">
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="checkbox" checked={draft.bloqueado} onChange={(e) => setDraft({ ...draft, bloqueado: e.target.checked })} className="w-3.5 h-3.5" />
                        <Ban size={12} /> Bloqueado
                      </label>
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="checkbox" checked={draft.oculto} onChange={(e) => setDraft({ ...draft, oculto: e.target.checked })} className="w-3.5 h-3.5" />
                        <EyeOff size={12} /> Oculto
                      </label>
                      {draft.cliente_id && (
                        <button
                          type="button"
                          onClick={() => desvincularCliente(c)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Desvincular cliente #{draft.cliente_id}
                        </button>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 mt-3">
                      <button type="button" className="btn-secundario text-xs" onClick={() => { setExpandidoId(null); setDraft(null); }} disabled={salvando}>
                        <XIcon size={12} /> Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn-primario text-xs flex items-center gap-1.5"
                        onClick={() => salvar(c)}
                        disabled={salvando || !draft.nome.trim()}
                      >
                        {salvando ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Salvar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  if (embutido) return corpo;

  return (
    <Modal aberto={aberto} onFechar={onFechar} titulo="Contatos" largura={820}
      rodape={<button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>}>
      {corpo}
    </Modal>
  );
}
